import { Wrench, Award, Users, MapPin, Shield, Target } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import heroImage from "@/assets/hero-mechanic.jpg";
import workshopImg from "@/assets/workshop.png";
import engineBayImg from "@/assets/engine-bay.png";

const values = [
  { icon: Wrench, title: "Engineering Precision", desc: "Every repair follows engineering standards — no guesswork, only data-driven diagnostics and solutions." },
  { icon: Award, title: "Certified Expertise", desc: "Patrick Kungu brings formal Automotive Engineering training to every job, big or small." },
  { icon: Users, title: "Customer First", desc: "We respect your time. That's why we come to you — at home, work, or roadside." },
  { icon: MapPin, title: "Local Knowledge", desc: "Born and raised in Thika. We know every corner from Section 9 to Juja and beyond." },
  { icon: Shield, title: "Trusted Service", desc: "Transparent pricing, honest diagnostics, and no hidden fees. Your trust is our reputation." },
  { icon: Target, title: "Precision Results", desc: "We don't guess — we diagnose with professional tools and fix it right the first time." },
];

const About = () => (
  <>
    <PageHeader title="About Us" subtitle="The story behind Precision Mobile Auto" breadcrumb="About" />

    {/* Meet the Team */}
    <section className="section-padding bg-background">
      <div className="container mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-16 items-center">
          <div className="order-2 md:order-1">
            <p className="font-heading uppercase tracking-[0.3em] text-primary text-xs sm:text-sm mb-3">Meet the Team</p>
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl uppercase text-foreground mb-2 leading-tight">
              Patrick Kungu &amp; <span className="text-gradient">Meja Jenerali</span>
            </h2>
            <div className="premium-divider max-w-[80px] mb-6" />
            <div className="space-y-4 text-muted-foreground text-sm sm:text-base leading-relaxed">
              <p>
                Precision Mobile Auto was founded by <strong className="text-foreground">Patrick Kungu</strong>, a certified Automotive Engineer with a passion for bringing professional-grade auto repair directly to your doorstep.
              </p>
              <p>
                Together with <strong className="text-foreground">Meja Jenerali</strong>, they combine hands-on mechanical expertise with modern diagnostic technology to deliver reliable, transparent service across Thika, Juja, and the surrounding areas.
              </p>
              <p>
                No workshop overhead means lower costs for you — and no towing hassles. From engine diagnostics to full health checks, we bring the workshop to your location.
              </p>
            </div>
          </div>
          <div className="order-1 md:order-2">
            <div className="relative">
              <div className="aspect-[4/3] rounded-xl overflow-hidden shadow-elevated">
                <img src={heroImage} alt="Patrick Kungu - Automotive Engineer" className="w-full h-full object-cover" loading="lazy" />
              </div>
              {/* Decorative frame */}
              <div className="absolute -bottom-4 -right-4 w-full h-full border-2 border-primary/20 rounded-xl -z-10" />
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Workshop Section */}
    <section className="relative overflow-hidden">
      <div className="grid grid-cols-1 md:grid-cols-2">
        <div className="bg-charcoal-gradient flex items-center p-8 sm:p-12 lg:p-16 relative noise-overlay order-2 md:order-1">
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-primary/5 rounded-full blur-[80px]" />
          <div className="relative z-10">
            <p className="font-heading uppercase tracking-[0.3em] text-primary text-xs sm:text-sm mb-3">Our Workspace</p>
            <h2 className="font-heading text-2xl sm:text-3xl uppercase text-secondary-foreground mb-4 leading-tight">
              Where <span className="text-gradient">Precision</span> Happens
            </h2>
            <p className="text-silver/70 text-sm sm:text-base leading-relaxed mb-4">
              Our workshop is equipped with professional-grade tools and diagnostic equipment. But what sets us apart is that we bring all of this directly to you.
            </p>
            <p className="text-silver/70 text-sm sm:text-base leading-relaxed">
              From OBD-II scanners to torque wrenches, every tool is maintained to the highest standard.
            </p>
          </div>
        </div>
        <div className="relative order-1 md:order-2">
          <img src={workshopImg} alt="Professional automotive workshop" className="w-full h-full object-cover min-h-[280px]" loading="lazy" />
        </div>
      </div>
    </section>

    {/* Engine Detail */}
    <section className="relative overflow-hidden">
      <div className="grid grid-cols-1 md:grid-cols-2">
        <div className="relative">
          <img src={engineBayImg} alt="Engine bay close-up" className="w-full h-full object-cover min-h-[280px]" loading="lazy" />
        </div>
        <div className="bg-card flex items-center p-8 sm:p-12 lg:p-16 relative">
          <div className="absolute top-0 right-0 w-48 h-48 bg-primary/3 rounded-full blur-[80px]" />
          <div className="relative z-10">
            <p className="font-heading uppercase tracking-[0.3em] text-primary text-xs sm:text-sm mb-3">Deep Expertise</p>
            <h2 className="font-heading text-2xl sm:text-3xl uppercase text-foreground mb-4 leading-tight">
              We Know <span className="text-gradient">Engines</span>
            </h2>
            <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
              With a deep understanding of modern engine architecture, electronic fuel injection, turbo systems, and emission controls — we diagnose and repair with engineering-level precision.
            </p>
          </div>
        </div>
      </div>
    </section>

    {/* Values */}
    <section className="section-padding bg-background">
      <div className="container mx-auto">
        <div className="text-center mb-10 sm:mb-14">
          <p className="font-heading uppercase tracking-[0.3em] text-primary text-xs sm:text-sm mb-3">What Drives Us</p>
          <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl uppercase text-foreground mb-4">Our Values</h2>
          <div className="premium-divider mx-auto max-w-[80px]" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {values.map((v) => (
            <div key={v.title} className="bg-card border border-border/50 rounded-xl p-5 sm:p-7 text-center hover:border-primary/40 hover:shadow-elevated transition-all duration-500 group hover:-translate-y-1">
              <div className="p-3.5 rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 w-fit mx-auto mb-4 group-hover:shadow-premium transition-all duration-500">
                <v.icon className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-heading text-base sm:text-lg uppercase text-foreground mb-2">{v.title}</h3>
              <p className="text-muted-foreground text-xs sm:text-sm leading-relaxed">{v.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  </>
);

export default About;
